import React, { forwardRef, useImperativeHandle, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  Snackbar,
  Theme,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Close } from '@material-ui/icons';
import moment from 'moment';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme: Theme) => ({
  createButton: {
    borderRadius: theme.spacing(3),
    color: 'white',
    margin: `${theme.spacing(3)}px 0px`,
    width: 168,
    height: 39,
    borderColor: '#7683F9',
  },
  dialogBackground: {
    borderRadius: 10,
    backgroundColor: '#454057',
    width: '100%',
  },
  dialogTitle: {
    backgroundColor: '#564F70',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  textfield: {
    backgroundColor: 'white',
    borderRadius: 4,
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    margin: `${theme.spacing(1)}px 0px`,
  },
}));
interface IProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (discussion: ITopicDiscussion) => void;
  submitting: boolean;
}
export interface IHandler {
  clear: () => void;
  badWord: () => void;
}
const initialDiscussion: ITopicDiscussion = {
  commentCount: 0,
  author: '',
  avatar: '',
  content: '',
  title: '',
  uid: '',
  createdAt: moment(),
};
const titleLimit = 120;
const contentLimit = 10000;
const contentMinLimit = 64;
const ForumCreateDialog: React.ForwardRefRenderFunction<IHandler, IProps> = (
  { open, onClose, onSubmit, submitting },
  ref
) => {
  const [discussion, setDiscussion] = useState<ITopicDiscussion>(
    initialDiscussion
  );
  const [error, setError] = useState('');
  const [showSnackbar, setShowSnackbar] = useState(false);
  const {
    createButton,
    dialogBackground,
    dialogTitle,
    loading,
    textfield,
  } = useStyles();
  useImperativeHandle(ref, () => ({
    clear: () => {
      setDiscussion(initialDiscussion);
      setError('');
    },
    badWord: () => {
      setShowSnackbar(true);
    },
  }));
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (discussion.content.length < contentMinLimit) {
      setError(`โปรดระบุรายละเอียดอย่างน้อย ${contentMinLimit} ตัวอักษร`);
      return;
    }
    onSubmit(discussion);
  };
  const handleTextfield = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    event.persist();
    const key = event.target.name as keyof ITopicDiscussion;
    setDiscussion((state) => ({ ...state, [key]: event.target.value }));
  };
  const handleKeyPress = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };
  return (
    <>
      <Dialog
        classes={{ paper: dialogBackground }}
        open={open}
        onClose={onClose}
      >
        <DialogTitle className={dialogTitle}>
          <Box display='flex' justifyContent='space-between'>
            สร้างกระทู้
            <IconButton size='small' color='secondary' onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <form onSubmit={handleSubmit} onKeyPress={handleKeyPress}>
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              width='100%'
              pt={1}
            >
              <Box alignSelf='flex-end'>
                <Typography variant='caption'>{`${discussion.title.length}/${titleLimit}`}</Typography>
              </Box>
              <InputBase
                className={textfield}
                autoFocus
                id='title'
                name='title'
                placeholder='หัวข้อฟอรั่ม'
                fullWidth
                required
                value={discussion.title}
                onChange={handleTextfield}
                inputProps={{
                  maxLength: titleLimit,
                }}
              />
              <Box alignSelf='flex-end'>
                <Typography variant='caption'>{`${discussion.content.length}/${contentLimit}`}</Typography>
              </Box>
              <InputBase
                className={textfield}
                id='content'
                name='content'
                value={discussion.content}
                placeholder='รายละเอียด'
                multiline
                rows={12}
                fullWidth
                inputProps={{
                  maxLength: contentLimit,
                }}
                onChange={handleTextfield}
              />

              <Box alignSelf='flex-end'>
                <Typography variant='caption' color='error'>
                  {error}
                </Typography>
              </Box>
              <Button
                className={createButton}
                variant='outlined'
                color='secondary'
                type='submit'
              >
                สร้าง
              </Button>
            </Box>
          </form>
          <Snackbar
            open={showSnackbar}
            autoHideDuration={3000}
            onClose={() => {
              setShowSnackbar(false);
            }}
          >
            <Alert severity='error'>พบคำไม่เหมาะสม</Alert>
          </Snackbar>
          <Backdrop open={submitting} className={loading}>
            <CircularProgress color='secondary' />
          </Backdrop>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default forwardRef(ForumCreateDialog);
